import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { StorefrontTemplateProps, Product, BookingSlot } from "../TemplateInterface";
import { ProductCard } from "../components/ProductCard";
import { ProductQuickView } from "../components/ProductQuickView";
import { BookingQuickView } from "../components/BookingQuickView";
import {
  StorefrontBookingCheckout,
  type BookingCheckoutPayload,
} from "../components/StorefrontBookingCheckout";
import { BookingSlotCard } from "../components/BookingSlotCard";
import { Button } from "@/components/ui/button";
import { useEnhancedCart } from "@/contexts/EnhancedCartContext";
import { EnhancedCheckoutPage } from "@/pages/EnhancedCheckoutPage";
import { formatPrice as formatPriceUtil } from "@/utils/currency";
import {
  getCartLineSubtotal,
  getEffectiveUnitPrice,
} from "@/utils/wholesalePricing";
import { fadeUp, staggerContainer, staggerItem, easeOut } from "@/components/landing/marketing-motion";
import api from "@/utils/api";
import { trackSalesEvent } from "@/lib/sales-events";
import { normalizeTemplateId } from "@/lib/sales-link";
import { EditorialChrome, ShowcaseChrome, type SalesLinkChrome } from "./SalesLinkLayouts";
import {
  RiPhoneFill,
  RiMailFill,
  RiMapPinFill,
  RiShoppingBagLine,
  RiCloseLine,
  RiInstagramFill,
  RiFacebookFill,
  RiTwitterXLine,
  RiYoutubeLine,
  RiTiktokFill,
  RiGlobalLine,
  RiSearchLine,
  RiGridLine,
  RiListUnordered,
  RiTimeLine,
  RiShoppingCartLine,
  RiWhatsappFill,
} from "@remixicon/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type ThemeDefaults = {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  fontFamily: string;
  borderRadius: string;
  cardStyle: string;
};

function softPrimaryShadow(primary: string) {
  return `0 16px 40px -24px ${primary}66`;
}

/** Catalog sections animate on mount so they stay visible even with little scroll room. */
const catalogReveal = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, ease: easeOut },
} as const;

function formatAddress(address?: {
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}) {
  if (!address) return "";
  return [address.street, address.city, address.state, address.country]
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean)
    .join(", ");
}

function ensureHttp(url: string) {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function SimpleTemplateContent({ store, theme }: StorefrontTemplateProps) {
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [productView, setProductView] = useState<"grid" | "list">("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const { items } = useEnhancedCart();
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(
    null,
  );
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingSlot, setBookingSlot] = useState<BookingSlot | null>(null);
  const [bookingCheckout, setBookingCheckout] =
    useState<BookingCheckoutPayload | null>(null);
  const [checkoutOptions, setCheckoutOptions] = useState<any>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const loadCheckoutOptions = async () => {
      try {
        const res = await api.get(
          `/api/public/stores/${store.business.url}/checkout-options`,
        );
        setCheckoutOptions(res.data?.checkoutOptions || res.data || null);
      } catch {
        setCheckoutOptions(null);
      }
    };
    if (store.business.url) loadCheckoutOptions();
  }, [store.business.url]);

  useEffect(() => {
    if (showCart) trackSalesEvent(store.business.url, "cart");
  }, [showCart, store.business.url]);

  useEffect(() => {
    if (showCheckout || bookingCheckout) trackSalesEvent(store.business.url, "checkout");
  }, [showCheckout, bookingCheckout, store.business.url]);

  // Deep-link: open booking modal from navigation state
  useEffect(() => {
    const slotId = (location.state as { bookingSlotId?: string } | null)
      ?.bookingSlotId;
    if (!slotId || !store.bookingSlots?.length) return;
    const found = store.bookingSlots.find(
      (s) => s.id === slotId || s._id === slotId,
    );
    if (found) {
      setBookingSlot(found);
      setBookingOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, store.bookingSlots]);

  const categories = Array.from(
    new Set(store.products.map((p) => p.category).filter(Boolean)),
  ) as string[];

  let filteredProducts = store.products.filter((p) => p.status === "active");

  if (selectedCategory) {
    filteredProducts = filteredProducts.filter(
      (p) => p.category === selectedCategory,
    );
  }

  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filteredProducts = filteredProducts.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        (p.description && p.description.toLowerCase().includes(query)),
    );
  }

  const themeWithDefaults: ThemeDefaults = {
    primaryColor:
      theme?.primaryColor ||
      store.business.settings.colors?.primary ||
      "#3b82f6",
    secondaryColor:
      theme?.secondaryColor ||
      store.business.settings.colors?.secondary ||
      "#10b981",
    backgroundColor: theme?.backgroundColor || "#ffffff",
    textColor: theme?.textColor || "#1a1a1a",
    fontFamily: "var(--font-sans), Manrope, system-ui, sans-serif",
    borderRadius: theme?.borderRadius || "1rem",
    cardStyle: theme?.cardStyle || "shadow",
  };

  const primary = themeWithDefaults.primaryColor;
  const accent = store.business.settings.colors?.accent || primary;
  const customization = store.business.settings.customization;
  const variant = normalizeTemplateId(
    customization?.salesLink?.activeTemplateId || customization?.template,
  );
  const bannerEnabled =
    Boolean(customization?.banner?.enabled) &&
    Boolean(customization?.banner?.imageUrl);
  const bannerUrl = customization?.banner?.imageUrl;
  const showHero = customization?.layoutOptions?.showHero !== false;
  const storeName = store.business.storeName || store.business.name;
  const heroTitle = customization?.hero?.title?.trim() || storeName;
  const bio =
    customization?.hero?.subtitle?.trim() || store.business.businessDescription || "";
  const logo = store.business.settings.logo;
  const formattedAddress = formatAddress(store.business.address);
  const social = store.business.socialLinks;
  const whatsapp = store.business.whatsappNumber;
  const email = store.business.email;
  const showSocial =
    customization?.layoutOptions?.showSocialLinks !== false;

  const hasSocialLinks = Boolean(
    showSocial &&
      (social?.instagram ||
        social?.facebook ||
        social?.twitter ||
        social?.youtube ||
        social?.tiktok ||
        social?.website),
  );

  const hasBusinessHours =
    Boolean(store.business.businessHours?.length) &&
    store.business.businessHours!.some((h) => h.open || h.day);

  const formatPrice = (price: number) =>
    formatPriceUtil(price, { currency: store.business.currency });

  const whatsappHref = whatsapp
    ? `https://wa.me/${whatsapp.replace(/[^\d]/g, "")}?text=${encodeURIComponent(
        `Hi! I'm interested in ${storeName}.`,
      )}`
    : null;

  const mapsHref = formattedAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formattedAddress)}`
    : null;

  const salesLinkChrome: SalesLinkChrome = {
    primary,
    secondary: themeWithDefaults.secondaryColor,
    accent,
    storeName,
    logo,
    heroTitle,
    bio,
    businessType: store.business.businessType,
    bannerEnabled,
    bannerUrl,
    showHero,
    showSearch: customization?.layoutOptions?.showSearch !== false,
    formattedAddress,
    whatsappHref,
    whatsapp,
    email,
    mapsHref,
    hasSocialLinks,
    social,
    hasBusinessHours,
    businessHours: store.business.businessHours,
    searchQuery,
    onSearch: setSearchQuery,
    onOpenMobileSearch: () => setShowMobileSearch(true),
    itemCount: items.length,
    onOpenCart: () => setShowCart(true),
    hideCart: Boolean(store.bookingSettings?.disableProducts),
  };

  return (
    <div
      className="min-h-screen bg-white"
      style={{
        fontFamily: themeWithDefaults.fontFamily,
        backgroundColor: variant === "editorial" ? "#faf9f6" : themeWithDefaults.backgroundColor,
        color: themeWithDefaults.textColor,
        ["--store-primary" as string]: primary,
        ["--store-secondary" as string]: themeWithDefaults.secondaryColor,
      }}
    >
      {customization?.announcements?.enabled && customization.announcements.text?.trim() && (
        <div
          className="px-4 py-2.5 text-center text-sm font-medium"
          style={{
            backgroundColor: customization.announcements.backgroundColor,
            color: customization.announcements.textColor,
          }}
        >
          {customization.announcements.text.trim()}
        </div>
      )}

      {variant === "classic" && (
        <>
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-black/5 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-[96%] items-center gap-3 sm:h-16 lg:max-w-[80%]">
          <div className="flex min-w-0 shrink-0 items-center gap-2.5">
            {logo ? (
              <img
                src={logo}
                alt={storeName}
                className="size-8 rounded-full object-cover ring-1 ring-black/5 sm:size-9"
              />
            ) : (
              <span
                className="flex size-8 items-center justify-center rounded-full text-sm font-bold text-white sm:size-9"
                style={{ backgroundColor: primary }}
              >
                {storeName.charAt(0).toUpperCase()}
              </span>
            )}
            <h1
              className="truncate font-heading text-base font-bold tracking-tight sm:text-lg"
              style={{ color: primary }}
            >
              {storeName}
            </h1>
          </div>

          {!store.bookingSettings?.disableProducts &&
            customization?.layoutOptions?.showSearch !== false && (
              <div className="mx-auto hidden max-w-md flex-1 sm:flex">
                <div className="relative w-full">
                  <RiSearchLine className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-black/35" />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-10 w-full rounded-full border border-black/8 bg-black/[0.03] pl-10 pr-9 text-sm outline-none transition-colors focus:border-black/15 focus:bg-white"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-black/35 hover:text-black/60"
                    >
                      <RiCloseLine className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            )}

          <div className="ml-auto flex items-center gap-2">
            {!store.bookingSettings?.disableProducts &&
              customization?.layoutOptions?.showSearch !== false && (
                <button
                  type="button"
                  onClick={() => setShowMobileSearch(true)}
                  className="rounded-full p-2 text-black/50 transition-colors hover:bg-black/[0.04] sm:hidden"
                  aria-label="Open search"
                >
                  <RiSearchLine className="h-5 w-5" />
                </button>
              )}

            {!store.bookingSettings?.disableProducts && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCart(true)}
                className="relative h-10 rounded-full border-black/10 px-3.5 text-black/80 hover:!bg-black/[0.03] hover:!text-black"
              >
                <RiShoppingCartLine className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Cart</span>
                {items.length > 0 && (
                  <span
                    className="ml-1.5 rounded-full px-1.5 py-0.5 text-xs font-semibold text-white"
                    style={{ backgroundColor: accent }}
                  >
                    {items.length}
                  </span>
                )}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Facebook-style store profile — always visible */}
      <section className="border-b border-black/5">
              <div className="relative mx-auto max-w-[96%] lg:max-w-[80%]">
                {showHero && (
                  <div
                    className="relative h-40 overflow-hidden rounded-b-[1.75rem] sm:h-52 md:h-64 md:rounded-b-[2.25rem]"
                    style={
                      bannerEnabled
                        ? {
                            backgroundImage: `url(${bannerUrl})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }
                        : {
                            background: `linear-gradient(135deg, ${primary}22 0%, ${themeWithDefaults.secondaryColor}18 45%, ${primary}10 100%)`,
                          }
                    }
                  >
                    {bannerEnabled && (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-black/10 to-transparent" />
                    )}
                    {!bannerEnabled && (
                      <>
                        <div
                          className="pointer-events-none absolute -right-10 top-6 size-40 rounded-full opacity-40 blur-3xl"
                          style={{ backgroundColor: primary }}
                          aria-hidden
                        />
                        <div
                          className="pointer-events-none absolute -left-8 bottom-0 size-36 rounded-full opacity-30 blur-3xl"
                          style={{
                            backgroundColor: themeWithDefaults.secondaryColor,
                          }}
                          aria-hidden
                        />
                      </>
                    )}
                  </div>
                )}

                {/* Centered circular logo overlapping cover */}
                <div
                  className={`relative z-10 flex justify-center ${
                    showHero
                      ? "-mt-14 sm:-mt-16 md:-mt-[4.5rem]"
                      : "pt-8 sm:pt-10"
                  }`}
                >
                  <div
                    className="rounded-full bg-white p-1.5 shadow-[0_12px_32px_-16px_rgba(0,0,0,0.35)] ring-1 ring-black/5"
                    style={{ boxShadow: softPrimaryShadow(primary) }}
                  >
                    {logo ? (
                      <img
                        src={logo}
                        alt={storeName}
                        className="size-28 rounded-full object-cover sm:size-32 md:size-36"
                      />
                    ) : (
                      <div
                        className="flex size-28 items-center justify-center rounded-full font-display text-4xl font-black text-white sm:size-32 md:size-36"
                        style={{ backgroundColor: primary }}
                      >
                        {storeName.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <motion.div
                {...fadeUp}
                className="mx-auto max-w-[96%] px-4 pb-10 pt-5 text-center lg:max-w-[80%]"
              >
                <p className="font-heading text-xs font-semibold uppercase tracking-[0.18em] text-black/45">
                  {store.business.businessType || "Store"}
                </p>
                <h2
                  className="mt-2 font-display text-3xl font-black leading-[0.95] tracking-tight sm:text-4xl md:text-[2.75rem]"
                  style={{ color: primary }}
                >
                  {heroTitle}
                </h2>

                {bio && (
                  <p className="mx-auto mt-3 max-w-2xl text-base font-semibold leading-relaxed text-black/55 sm:text-lg">
                    {bio}
                  </p>
                )}

                {formattedAddress && (
                  <p className="mx-auto mt-3 flex max-w-xl items-start justify-center gap-1.5 text-sm text-black/50">
                    <RiMapPinFill className="mt-0.5 h-4 w-4 shrink-0" style={{ color: primary }} />
                    <span>{formattedAddress}</span>
                  </p>
                )}

                {/* Contact / social actions */}
                <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
                  {whatsappHref && (
                    <a
                      href={whatsappHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex h-11 items-center gap-2 rounded-full px-5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                      style={{
                        backgroundColor: primary,
                        boxShadow: softPrimaryShadow(primary),
                      }}
                    >
                      <RiWhatsappFill className="h-4 w-4" />
                      WhatsApp
                    </a>
                  )}
                  {whatsapp && (
                    <a
                      href={`tel:${whatsapp}`}
                      className="inline-flex h-11 items-center gap-2 rounded-full border border-black/10 bg-white px-5 text-sm font-semibold text-black/75 transition-colors hover:bg-black/[0.03]"
                    >
                      <RiPhoneFill className="h-4 w-4" style={{ color: primary }} />
                      Call
                    </a>
                  )}
                  {email && (
                    <a
                      href={`mailto:${email}`}
                      className="inline-flex h-11 items-center gap-2 rounded-full border border-black/10 bg-white px-5 text-sm font-semibold text-black/75 transition-colors hover:bg-black/[0.03]"
                    >
                      <RiMailFill className="h-4 w-4" style={{ color: primary }} />
                      Email
                    </a>
                  )}
                  {mapsHref && (
                    <a
                      href={mapsHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex h-11 items-center gap-2 rounded-full border border-black/10 bg-white px-5 text-sm font-semibold text-black/75 transition-colors hover:bg-black/[0.03]"
                    >
                      <RiMapPinFill className="h-4 w-4" style={{ color: primary }} />
                      Directions
                    </a>
                  )}
                </div>

                {hasSocialLinks && (
                  <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                    {social?.instagram && (
                      <SocialIconLink
                        href={social.instagram}
                        label="Instagram"
                        primary={primary}
                      >
                        <RiInstagramFill className="h-4 w-4" />
                      </SocialIconLink>
                    )}
                    {social?.facebook && (
                      <SocialIconLink
                        href={social.facebook}
                        label="Facebook"
                        primary={primary}
                      >
                        <RiFacebookFill className="h-4 w-4" />
                      </SocialIconLink>
                    )}
                    {social?.twitter && (
                      <SocialIconLink
                        href={social.twitter}
                        label="X"
                        primary={primary}
                      >
                        <RiTwitterXLine className="h-4 w-4" />
                      </SocialIconLink>
                    )}
                    {social?.tiktok && (
                      <SocialIconLink
                        href={social.tiktok}
                        label="TikTok"
                        primary={primary}
                      >
                        <RiTiktokFill className="h-4 w-4" />
                      </SocialIconLink>
                    )}
                    {social?.youtube && (
                      <SocialIconLink
                        href={social.youtube}
                        label="YouTube"
                        primary={primary}
                      >
                        <RiYoutubeLine className="h-4 w-4" />
                      </SocialIconLink>
                    )}
                    {social?.website && (
                      <SocialIconLink
                        href={ensureHttp(social.website)}
                        label="Website"
                        primary={primary}
                      >
                        <RiGlobalLine className="h-4 w-4" />
                      </SocialIconLink>
                    )}
                  </div>
                )}

                {hasBusinessHours && (
                  <div className="mx-auto mt-8 max-w-md rounded-[1.5rem] border border-black/5 bg-black/[0.02] px-5 py-4 text-left">
                    <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold text-black/80">
                      <RiTimeLine className="h-4 w-4" style={{ color: primary }} />
                      Business hours
                    </h3>
                    <div className="space-y-1.5">
                      {store.business.businessHours!.map((hours, index) => (
                        <div
                          key={`${hours.day}-${index}`}
                          className="flex items-center justify-between text-sm"
                        >
                          <span className="font-medium text-black/60">{hours.day}</span>
                          <span className="text-black/45">
                            {hours.open ? (
                              `${hours.openTime} – ${hours.closeTime}`
                            ) : (
                              <span className="text-black/30">Closed</span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            </section>
        </>
      )}
      {variant === "editorial" && <EditorialChrome chrome={salesLinkChrome} />}
      {variant === "showcase" && <ShowcaseChrome chrome={salesLinkChrome} />}

      {showMobileSearch && (
        <div className="fixed inset-0 z-[60] sm:hidden">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setShowMobileSearch(false)}
          />
          <div className="absolute left-0 right-0 top-0 flex items-center gap-3 border-b border-black/5 bg-white px-4 py-3">
            <div className="relative flex-1">
              <RiSearchLine className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-black/35" />
              <input
                type="text"
                autoFocus
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-11 w-full rounded-full border border-black/8 bg-black/[0.03] pl-10 pr-9 text-sm outline-none focus:bg-white"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-black/35"
                >
                  <RiCloseLine className="h-4 w-4" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowMobileSearch(false)}
              className="shrink-0 text-sm font-semibold text-black/60"
            >
              Done
            </button>
          </div>
        </div>
      )}

          <main className={`mx-auto px-4 py-10 sm:py-14 ${variant === "classic" ? "max-w-[96%] lg:max-w-[80%]" : variant === "editorial" ? "max-w-5xl" : "max-w-6xl"}`}>
            {showCheckout ? (
              <EnhancedCheckoutPage
                goBack={() => setShowCheckout(false)}
                primaryColor={primary}
              />
            ) : bookingCheckout ? (
              <StorefrontBookingCheckout
                payload={bookingCheckout}
                goBack={() => {
                  setBookingCheckout(null);
                  setBookingSlot(bookingCheckout.slot);
                  setBookingOpen(true);
                }}
                storeUrl={store.business.url}
                currency={store.business.currency}
                whatsappNumber={store.business.whatsappNumber}
                primaryColor={primary}
                paymentOptions={
                  checkoutOptions?.paymentOptions ||
                  {}
                }
                whatsappCheckoutEnabled={Boolean(
                  checkoutOptions?.whatsapp?.enabled,
                )}
              />
            ) : (
              <>
                {(() => {
                  const showProducts =
                    !store.bookingSettings?.disableProducts;
                  const hasActiveProducts =
                    store.products.filter((p) => p.status === "active")
                      .length > 0;
                  const hasBookings = Boolean(
                    store.bookingSlots && store.bookingSlots.length > 0,
                  );
                  const bookingsFirst =
                    hasBookings && (!showProducts || !hasActiveProducts);

                  const productsSection =
                    showProducts &&
                    (hasActiveProducts ||
                      Boolean(searchQuery) ||
                      Boolean(selectedCategory) ||
                      !hasBookings) ? (
              <motion.section {...catalogReveal} className="mb-14 sm:mb-20">
                <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
                  <div>
                    <p
                      className="font-heading text-xs font-semibold uppercase tracking-[0.18em]"
                      style={{ color: `${primary}99` }}
                    >
                      Shop
                    </p>
                    <h3 className="mt-1 font-display text-2xl font-black tracking-tight text-black sm:text-3xl">
                      Products
                      {filteredProducts.length > 0 && (
                        <span className="ml-2 text-lg font-semibold text-black/35">
                          ({filteredProducts.length})
                        </span>
                      )}
                    </h3>
                  </div>

                  <div className="flex items-center gap-2">
                    {searchQuery && (
                      <div className="flex items-center gap-1.5 rounded-full bg-black/[0.04] px-3 py-1 text-xs text-black/50 sm:hidden">
                        <RiSearchLine className="h-3 w-3" />
                        <span className="max-w-[120px] truncate">{searchQuery}</span>
                        <button type="button" onClick={() => setSearchQuery("")}>
                          <RiCloseLine className="h-3 w-3" />
                        </button>
                      </div>
                    )}

                    {customization?.layoutOptions?.showCategories !== false &&
                      categories.length > 0 && (
                        <Select
                          value={selectedCategory || "all"}
                          onValueChange={(value) =>
                            setSelectedCategory(value === "all" ? null : value)
                          }
                        >
                          <SelectTrigger className="!h-10 w-40 !rounded-full border-black/10 text-sm">
                            <SelectValue placeholder="All Categories" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Categories</SelectItem>
                            {categories.map((category) => (
                              <SelectItem key={category} value={category}>
                                {category}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                    <div className="flex h-10 overflow-hidden rounded-full border border-black/10">
                      <button
                        type="button"
                        onClick={() => setProductView("grid")}
                        className={`flex items-center px-3 ${
                          productView === "grid"
                            ? "bg-black/[0.06] text-black"
                            : "text-black/35 hover:bg-black/[0.03]"
                        }`}
                        aria-label="Grid view"
                      >
                        <RiGridLine className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setProductView("list")}
                        className={`flex items-center border-l border-black/10 px-3 ${
                          productView === "list"
                            ? "bg-black/[0.06] text-black"
                            : "text-black/35 hover:bg-black/[0.03]"
                        }`}
                        aria-label="List view"
                      >
                        <RiListUnordered className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {filteredProducts.length === 0 ? (
                  <div className="rounded-[1.75rem] border border-black/5 bg-black/[0.02] py-16 text-center">
                    <RiSearchLine className="mx-auto mb-3 h-10 w-10 text-black/20" />
                    <p className="text-sm font-semibold text-black/70">No products found</p>
                    <p className="mt-1 text-xs text-black/40">
                      {searchQuery || selectedCategory
                        ? "Try adjusting your search or filter."
                        : "No products are available at the moment."}
                    </p>
                  </div>
                ) : (
                  <motion.div
                    variants={staggerContainer}
                    initial="hidden"
                    animate="visible"
                    className={
                      productView === "grid"
                        ? variant === "editorial"
                          ? "grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
                          : variant === "showcase"
                            ? "grid grid-cols-2 gap-4 lg:grid-cols-3"
                            : "grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4 xl:grid-cols-5"
                        : "space-y-3"
                    }
                  >
                    {filteredProducts.map((product) => (
                      <motion.div key={product.id} variants={staggerItem}>
                        <ProductCard
                          product={product}
                          variant={productView}
                          currency={store.business.currency}
                          themeColors={themeWithDefaults}
                          onProductClick={(p) => {
                            setQuickViewProduct(p);
                            setQuickViewOpen(true);
                          }}
                        />
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </motion.section>
                  ) : null;

                  const bookingsSection = hasBookings ? (
              <motion.section {...catalogReveal} className="mb-10 sm:mb-14">
                <div className="mb-6">
                  <p
                    className="font-heading text-xs font-semibold uppercase tracking-[0.18em]"
                    style={{ color: `${primary}99` }}
                  >
                    Reserve
                  </p>
                  <h3 className="mt-1 font-display text-2xl font-black tracking-tight text-black sm:text-3xl">
                    Bookings
                    <span className="ml-2 text-lg font-semibold text-black/35">
                      ({store.bookingSlots!.length})
                    </span>
                  </h3>
                </div>

                <motion.div
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                  className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4"
                >
                  {store.bookingSlots!.map((slot: BookingSlot) => (
                    <motion.div key={slot.id} variants={staggerItem}>
                      <BookingSlotCard
                        slot={slot}
                        currency={store.business.currency}
                        themeColors={themeWithDefaults}
                        onSlotClick={(s) => {
                          setBookingSlot(s);
                          setBookingOpen(true);
                        }}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              </motion.section>
                  ) : null;

                  return bookingsFirst ? (
                    <>
                      {bookingsSection}
                      {productsSection}
                    </>
                  ) : (
                    <>
                      {productsSection}
                      {bookingsSection}
                    </>
                  );
                })()}
              </>
            )}
          </main>

      {!store.bookingSettings?.disableProducts && showCart && (
        <CartSidebar
          onClose={() => setShowCart(false)}
          theme={themeWithDefaults}
          formatPrice={formatPrice}
          onStartCheckout={() => {
            setBookingCheckout(null);
            setShowCheckout(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      )}

      <ProductQuickView
        open={quickViewOpen}
        onClose={() => setQuickViewOpen(false)}
        product={quickViewProduct}
        themeColors={{
          primary: themeWithDefaults.primaryColor,
          secondary: themeWithDefaults.secondaryColor,
        }}
        formatPrice={formatPrice}
      />
      <BookingQuickView
        open={bookingOpen}
        onClose={() => {
          setBookingOpen(false);
          setBookingSlot(null);
        }}
        slot={bookingSlot}
        currency={store.business.currency}
        themeColors={{
          primaryColor: themeWithDefaults.primaryColor,
          secondaryColor: themeWithDefaults.secondaryColor,
        }}
        onProceedToCheckout={(payload) => {
          setBookingCheckout(payload);
          setShowCheckout(false);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />

      <footer className="mt-auto border-t border-black/5 bg-white">
        <div className="mx-auto max-w-[96%] px-4 py-6 text-center lg:max-w-[80%]">
          <a
            href="https://sqaleshop.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-semibold uppercase tracking-[0.16em] text-black/35 transition-colors hover:text-black/60"
          >
            Powered by Sqaleshop
          </a>
        </div>
      </footer>
    </div>
  );
}

function SocialIconLink({
  href,
  label,
  primary,
  children,
}: {
  href: string;
  label: string;
  primary: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="inline-flex size-10 items-center justify-center rounded-full border border-black/8 text-white transition-opacity hover:opacity-90"
      style={{ backgroundColor: primary }}
    >
      {children}
    </a>
  );
}

function CartSidebar({
  onClose,
  theme,
  formatPrice,
  onStartCheckout,
}: {
  onClose: () => void;
  theme: ThemeDefaults;
  formatPrice: (price: number) => string;
  onStartCheckout: () => void;
}) {
  const { items, removeFromCart, updateQuantity, getTotal, getItemId } =
    useEnhancedCart();

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute right-0 top-0 flex h-full w-full max-w-sm flex-col border-l border-black/5 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-black/5 px-5 py-4">
          <h2 className="font-heading text-base font-bold text-black">
            Cart
            {items.length > 0 && (
              <span className="ml-2 text-sm font-normal text-black/35">
                ({items.length})
              </span>
            )}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 rounded-full p-0 text-black/40 hover:!bg-black/[0.04] hover:!text-black/70"
          >
            <RiCloseLine className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="py-16 text-center">
              <RiShoppingBagLine className="mx-auto mb-3 h-10 w-10 text-black/15" />
              <p className="text-sm font-medium text-black/50">Your cart is empty</p>
              <p className="mt-1 text-xs text-black/35">
                Add some products to get started
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => {
                const itemId = getItemId(item);
                return (
                  <div
                    key={itemId}
                    className="flex items-start gap-3 border-b border-black/5 pb-4 last:border-0"
                  >
                    <img
                      src={item.image || "/placeholder.png"}
                      alt={item.name}
                      className="size-14 shrink-0 rounded-xl object-cover ring-1 ring-black/5"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-black">
                        {item.name}
                      </p>
                      {item.variantName && (
                        <p className="mt-0.5 text-xs text-black/40">
                          {item.variantName}
                        </p>
                      )}
                      <p className="mt-0.5 text-sm text-black/55">
                        {formatPrice(
                          getEffectiveUnitPrice(
                            item.price,
                            item.quantity,
                            item.wholesale,
                          ),
                        )}{" "}
                        × {item.quantity} ={" "}
                        {formatPrice(getCartLineSubtotal(item))}
                      </p>
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            updateQuantity(itemId, item.quantity - 1)
                          }
                          disabled={item.quantity <= 1}
                          className="flex size-7 items-center justify-center rounded-full border border-black/10 text-sm text-black/50 hover:bg-black/[0.03] disabled:opacity-40"
                        >
                          −
                        </button>
                        <span className="w-4 text-center text-xs font-semibold">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            updateQuantity(itemId, item.quantity + 1)
                          }
                          className="flex size-7 items-center justify-center rounded-full border border-black/10 text-sm text-black/50 hover:bg-black/[0.03]"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFromCart(itemId)}
                      className="mt-0.5 text-black/25 transition-colors hover:text-red-400"
                    >
                      <RiCloseLine className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="space-y-3 border-t border-black/5 px-5 py-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-black/45">Total</span>
              <span className="text-base font-bold text-black">
                {formatPrice(getTotal())}
              </span>
            </div>
            <Button
              className="h-11 w-full rounded-full text-sm font-semibold text-white hover:!text-white hover:opacity-90"
              style={{
                backgroundColor: theme.primaryColor,
                boxShadow: softPrimaryShadow(theme.primaryColor),
              }}
              onClick={() => {
                onClose();
                onStartCheckout();
              }}
            >
              Proceed to Checkout
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function SimpleTemplate({ store, theme }: StorefrontTemplateProps) {
  return <SimpleTemplateContent store={store} theme={theme} />;
}
